import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { brands as mockBrands } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Marcas = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/brands`);
      if (response.data && response.data.length > 0) {
        setBrands(response.data);
      } else {
        setBrands(mockBrands);
      }
    } catch (error) {
      setBrands(mockBrands);
    } finally {
      setLoading(false);
    }
  };

  // Track page view
  useEffect(() => {
    const trackView = async () => {
      try {
        await axios.post(`${BACKEND_URL}/api/track/view?page=marcas`);
      } catch (e) {}
    };
    trackView();
  }, []);

  const featuredBrands = brands.filter(b => b.featured);
  const otherBrands = brands.filter(b => !b.featured);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-12 md:py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl md:text-5xl font-bold mb-4">Nossas Marcas</h1>
          <p className="text-lg md:text-xl text-blue-100 max-w-2xl">
            Trabalhamos apenas com as marcas mais confiáveis e reconhecidas do mercado
          </p>
        </div>
      </section>

      {/* Featured Brands */}
      {featuredBrands.length > 0 && (
        <section className="py-12 md:py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">Marcas em Destaque</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 mb-12 md:mb-16 max-w-5xl mx-auto">
              {featuredBrands.map((brand) => (
                <Card key={brand.id} className="overflow-hidden hover:shadow-2xl transition-shadow h-full">
                  <CardContent className="p-6 md:p-8">
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 mb-6 flex items-center justify-center h-[100px] md:h-[120px]">
                      <img
                        src={brand.logo}
                        alt={brand.name}
                        className="max-h-14 md:max-h-16 max-w-[180px] w-auto object-contain"
                      />
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">{brand.name}</h3>
                    {brand.description && (
                      <p className="text-gray-600 mb-6 leading-relaxed line-clamp-3">{brand.description}</p>
                    )}
                    <Link to={`/catalogo?marca=${brand.name}`}>
                      <Button className="bg-red-600 hover:bg-red-700 text-white w-full">
                        Ver Produtos {brand.name}
                        <ArrowRight className="ml-2" size={18} />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Other Brands */}
      <section className={featuredBrands.length === 0 ? 'py-12 md:py-16' : 'pb-12 md:pb-16'}>
        <div className="container mx-auto px-4">
          <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">
              {featuredBrands.length > 0 ? 'Outras Marcas' : 'Nossas Marcas'}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 md:gap-6">
              {(featuredBrands.length > 0 ? otherBrands : brands).map((brand) => (
                <Link
                  key={brand.id}
                  to={`/catalogo?marca=${brand.name}`}
                  className="group"
                  data-testid={`brand-link-${brand.name}`}
                >
                  <Card className="hover:shadow-xl transition-all hover:-translate-y-1 h-full">
                    <CardContent className="p-4 md:p-6 flex flex-col items-center justify-center h-[120px] md:h-[140px]">
                      <div className="flex-1 flex items-center justify-center w-full">
                        <img
                          src={brand.logo}
                          alt={brand.name}
                          className="max-h-10 md:max-h-12 max-w-[80px] md:max-w-[100px] w-auto object-contain grayscale group-hover:grayscale-0 transition-all"
                        />
                      </div>
                      <span className="mt-3 text-xs md:text-sm text-gray-500 group-hover:text-gray-900 transition-colors text-center">
                        {brand.name}
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-red-600 to-red-700 text-white py-12 md:py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-4xl font-bold mb-4">
            Encontre Produtos de Todas as Marcas
          </h2>
          <p className="text-base md:text-xl mb-8 text-red-100 max-w-2xl mx-auto">
            Confira nosso catálogo completo com produtos originais e garantia de fábrica
          </p>
          <Link to="/catalogo">
            <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 font-semibold text-base md:text-lg px-6 md:px-8 py-4 md:py-6">
              Acessar Catálogo
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
